import { DestinationEvent } from '@eventbrite/event-renderer';
import { addDays, eachDayOfInterval, format, startOfToday } from 'date-fns';
import {
    CATEGORIES_WITH_FORMAT_ID,
    CATEGORY_CAROUSEL_TITLE_MAPPING,
    EVENTBRITE_FORMAT_MAPPING,
} from '../../constants';

const DEFAULT_EVENT: DestinationEvent = {
    ticket_availability: {
        is_sold_out: false,
        is_free: false,
        minimum_ticket_price: {
            currency: 'USD',
            major_value: '99.00',
            value: 9900,
            display: '99.00 USD',
        },
        has_available_tickets: true,
        maximum_ticket_price: {
            currency: 'USD',
            major_value: '99.00',
            value: 9900,
            display: '99.00 USD',
        },
    },
    image: {
        edge_color_set: true,
        edge_color: '#68676a',
        url: 'https://img.evbuc.com/https%3A%2F%2Fcdn.evbuc.com%2Fimages%2F122020957%2F239331669958%2F1%2Foriginal.20200624-185744?w=466&auto=format%2Ccompress&q=75&sharp=10&rect=2%2C0%2C466%2C233&s=a456a986015ee86df75a4e97d42369e4',
        original: {
            url: 'https://img.evbuc.com/https%3A%2F%2Fcdn.evbuc.com%2Fimages%2F122020957%2F239331669958%2F1%2Foriginal.20200624-185744?auto=format%2Ccompress&q=75&sharp=10&s=5c1956f8beefaeea824a9cc19344977c',
            width: 470,
            height: 233,
        },
        crop_mask: {
            width: 466,
            height: 233,
            top_left: {
                y: 0,
                x: 2,
            },
        },
        aspect_ratio: '2',
        id: '122020957',
    },
    timezone: 'Europe/Berlin',
    id: '414',
    tickets_url:
        'https://www.eventbrite.com/checkout-external?eid=153013731189',
    tickets_by: 'Eventbrite',
    primary_organizer_id: '30575694064',
    num_children: 1,
    parent_url:
        'https://www.eventbrite.de/e/arabischsprachiges-freitagsgebet-izm-1330-tickets-134911781771',
    hide_end_date: false,
    start_date: '2021-05-21',
    end_time: '14:00',
    _type: 'destination_event',
    end_date: '2021-05-21',
    tags: [
        {
            prefix: 'EventbriteSubCategory',
            tag: 'EventbriteSubCategory/7002',
            display_name: 'Mental health',
        },
        {
            prefix: 'EventbriteCategory',
            tag: 'EventbriteCategory/107',
            display_name: 'Health & Wellness',
            localized: {
                display_name: 'Health & Wellness',
            },
        },
    ],
    url: 'https://www.eventbrite.de/e/arabischsprachiges-freitagsgebet-izm-1330-tickets-153013731189',
    name: 'Default Event Name',
    eventbrite_event_id: '413',
    start_time: '13:30',
    primary_venue: {
        _type: 'destination_venue',
        name: 'Freimann-Moschee',
        address: {
            city: 'München',
            country: 'DE',
            region: 'BY',
            longitude: '11.6340722',
            localized_address_display: 'Wallnerstraße 1-3, 80939 München',
            postal_code: '80939',
            address_1: 'Wallnerstraße 1-3',
            latitude: '48.21036230000001',
            localized_multi_line_address_display: [
                'Wallnerstraße 1-3',
                '80939 München',
            ],
            localized_area_display: 'München',
        },
        id: '55873985',
    },
    image_id: '122020957',
    is_protected_event: false,
    is_cancelled: null,
    primary_venue_id: '55873985',
    checkout_flow: 'widget',
    series_id: '134911781771',
    language: 'de-de',
    hide_start_date: false,
    summary: 'Test Summary',
    is_online_event: false,
    eid: '412',
    published: '2021-01-02T04:59:19Z',
};

// Constant for random category
export const RANDOM = 'random';

/**
 * Generates mock data for a category of destination events.
 *
 * @param numberOfEvents The number of events to generate.
 * @param category The category of events to generate. Defaults to RANDOM.
 * @param allFree Determines if all events should be free. Defaults to false.
 * @returns An array of DestinationEvent objects representing the mock data.
 */
export const getCategoryMockData = (
    numberOfEvents: number,
    category: typeof CATEGORIES_WITH_FORMAT_ID[number] | typeof RANDOM = RANDOM,
    allFree = false,
): DestinationEvent[] => {
    // Create an array with defined length and fill it with default event data
    let newMockData = new Array(numberOfEvents).fill(null);

    newMockData = newMockData.map((_, index) => {
        // Create a unique ID for each event
        const id = `${Date.now()}0${index}`;

        // Get category keys that have an eventbrite format ID
        const categoriesWithFormatID = Object.values(EVENTBRITE_FORMAT_MAPPING);

        const areRandom =
            category === RANDOM ||
            !CATEGORIES_WITH_FORMAT_ID.includes(category);

        // Randomly assign a category from the ordered categories array or set to passed in category
        const categoryName = areRandom
            ? categoriesWithFormatID[
                  Math.floor(Math.random() * categoriesWithFormatID.length)
              ]
            : category;

        // Get the eventbrite format ID from the category name
        const categoryFormat = Object.keys(EVENTBRITE_FORMAT_MAPPING).find(
            (key) => EVENTBRITE_FORMAT_MAPPING[key] === categoryName,
        );

        // Get a random day in the next week
        const startDate = startOfToday();
        const endDate = addDays(startDate, 7);

        const listOfDates = eachDayOfInterval({
            start: startDate,
            end: endDate,
        }).map((date) => format(date, 'yyyy-MM-dd'));

        const randomFutureDay =
            listOfDates[Math.floor(Math.random() * listOfDates.length)];

        // Randomly assign free or paid event
        const isFree = allFree || Math.random() < 0.5;

        const newEvent = {
            ...DEFAULT_EVENT,
            ...{
                id,

                // These URLs are not real, but they are needed for the event renderer to work
                url: `https://www.eventbrite.com/e/${id}`,
                parent_url: `https://www.eventbrite.com/e/${id}`,

                // Added descriptors to the event name to make it easier to see which events are being rendered
                name: `I am a ${
                    isFree ? 'free' : ''
                } ${categoryName.toLowerCase()} event - ${index}`,

                // Add the category format from above to the event tags, keeping the existing tags
                tags: [
                    ...(DEFAULT_EVENT.tags || []),
                    {
                        prefix: 'EventbriteFormat',
                        tag: categoryFormat,
                        display_name:
                            CATEGORY_CAROUSEL_TITLE_MAPPING[categoryName],
                    },
                ],

                // Add a random start and end date in the next week
                start_date: randomFutureDay,
                end_date: randomFutureDay,

                // Add a random image from picsum to help with visual testing
                image: {
                    ...DEFAULT_EVENT.image,
                    url: `https://picsum.photos/id/${
                        Math.floor(Math.random() * 100) + 1
                    }/400/200`,
                },
            },
        };

        // If the event is free, set the ticket availability to free
        if (isFree) {
            newEvent.ticket_availability = {
                is_sold_out: false,
                is_free: true,
                minimum_ticket_price: {
                    currency: 'USD',
                    major_value: '0.00',
                    value: 0,
                    display: '0.00 USD',
                },
                has_available_tickets: false,
                maximum_ticket_price: {
                    currency: 'USD',
                    major_value: '0.00',
                    value: 0,
                    display: '0.00 USD',
                },
            };
        }

        return newEvent;
    });

    return newMockData;
};
